import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { closeDialog } from '@shared/store/dialog.duck';
import DialogHeader from '@shared/dialogs/DialogHeader';
import Dialog from '@shared/ui/molecules/Dialog';
import TextField from '@shared/ui/atoms/TextField';
import { Checkbox } from '@shared/ui/atoms/Checkbox';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { LoadingOverlay } from '@shared/ui/molecules/LoadingOverlay';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { failureNotification } from '@shared/store/notification.duck';
import { BasicUserInfo } from '@shared/models/user.models';
import { findUserRequest, UpdateUserField, updateUserField } from '@features/settings/services/UserService';
import { Department } from '@features/whereabouts/pages/departments/data/departments.models';
import { getAllUsersInDepartment } from '../departmentUsersService';

interface PickerUser {
  userId: string;
  firstName: string;
  lastName: string;
}

interface Props {
  payload?: {
    department: Department;
    onClose?: () => void;
  };
}

export default function AddDepartmentMembersDialog(props: Props) {
  const dispatch = useDispatch();
  const department = props.payload?.department;

  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<PickerUser[]>([]);
  const [members, setMembers] = useState<PickerUser[]>([]);
  const [loading, setLoading] = useState(false);

  // Load current members so people already in the department show as ticked.
  useEffect(() => {
    if (!department) return;
    setLoading(true);
    getAllUsersInDepartment(department.id)
      .then(users => setMembers(users.map(u => ({ userId: u.id, firstName: u.firstName, lastName: u.lastName }))))
      .finally(() => setLoading(false));
  }, [department]);

  // Search the company directory (debounced; the backend requires 3+ chars).
  useEffect(() => {
    const id = setTimeout(() => {
      findUserRequest(searchQuery)
        .then((result: BasicUserInfo[]) =>
          setResults(result.map(u => ({ userId: u.userId, firstName: u.firstName, lastName: u.lastName }))));
    }, 250);
    return () => clearTimeout(id);
  }, [searchQuery]);

  const isMember = (userId: string) => members.some(m => m.userId === userId);

  const toggleUser = async (user: PickerUser) => {
    if (!department || loading) return;
    setLoading(true);
    try {
      if (isMember(user.userId)) {
        // Assigning department 0 clears it on the backend.
        await updateUserField(user.userId, UpdateUserField.department, 0);
        setMembers(prev => prev.filter(m => m.userId !== user.userId));
      } else {
        await updateUserField(user.userId, UpdateUserField.department, department.id);
        setMembers(prev => [...prev, user]);
      }
    } catch {
      dispatch(failureNotification('Failed to update department members'));
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    if (props.payload?.onClose) props.payload.onClose();
    dispatch(closeDialog());
  };

  // Show current members until the admin starts searching for someone new.
  const list = searchQuery.length === 0 ? members : results;

  return (
    <Dialog isOpen={true} onClose={close}>
      <DialogHeader title={'settings.departments.add-members'} />
      {department && <BodyRegular style={{ marginBottom: 16 }}>{department.name}</BodyRegular>}

      {loading && <LoadingOverlay />}

      <TextField value={searchQuery} onChange={setSearchQuery} />
      <SearchResults>
        {list.map((user, key) => (
          <UserInfoRow key={key}>
            <UserAvatarAndName firstName={user.firstName} lastName={user.lastName} />
            <Checkbox value={isMember(user.userId)} onChange={() => toggleUser(user)} />
          </UserInfoRow>
        ))}
      </SearchResults>

      <PrimaryButton click={close} text="button.done" size={ButtonSize.Large} fullWidth={true} />
    </Dialog>
  );
}

const SearchResults = styled.div`
  margin-bottom: 32px;
  height: 250px;
  overflow-y: auto;
`;

const UserInfoRow = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  .userAvatarAndName {
    cursor: pointer;
  }
`;
